// Libs
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DragDropContext } from 'react-beautiful-dnd';
import { isMobile } from 'react-device-detect';
import { isEqual, isEmpty, cloneDeep, includes, every } from 'lodash';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import queryString from 'query-string';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { Link } from '@mui/material';
import Tooltip from '@material-ui/core/Tooltip';
import IconButton from '@material-ui/core/IconButton';
import classnames from 'classnames';
import { useIsOnline } from 'react-use-is-online';
import { useModal } from '../modal/ModalContext';
import { addFootbar } from '../footbar/actions';
import { isClosedFloorField, isClosedPositionField, getKanbanVersionWsValue } from '../core/helper';
import { canSeeMobileBottomNavigation } from '../users/helper';

// Components
import DroppableColumn from './components/DroppableColumn';
import KanbanVersionToggle from './components/KanbanVersionToggle';
// Actions
import {
  moveTicket,
  updateTicket,
  saveTicketOrder,
  setTicketShownInDetailPopup,
  getTicketSummary,
  getTicketComments,
  addTicketEditableField,
  getTicketOperations,
  setPlayingSoundOff,
  getTickets,
  setVisibleTickets,
  getTicketFromKey,
  moveTickets,
  updateTickets, ticketsMoveDiffColErrorNotif, networkConnectionErrorNotif
} from './actions';
import { useComponentsPool } from '../../ComponentsPool';
// SCSS
import './style.scss';

import createLoadingSelector from '../layout/actions';
import { Toast } from '../notification/components';
import { useFootBar } from '../footbar/FootBarContext';
import { updateNotification } from '../navbar/actions';
import { EVENT_OPEN_TICKET, segmentTrack } from '../layout/helper';
import { ticketMovementType } from './enums';
import { notification } from '../notification/actions';

const loadingSelector = createLoadingSelector(['GET_TICKETS', 'GET_TICKET_FROM_KEY', 'SAVE_TICKET_ORDER', 'UPDATE_TICKET', 'UPDATE_TICKETS', 'MOVE_TICKETS', 'GET_TICKET_OPERATIONS', 'GET_TICKET_COMMENTS', 'GET_TICKET_SUMMARY']);
const loadingSelectorOnMobile = createLoadingSelector(['UPDATE_TICKET', 'GET_TICKET_OPERATIONS', 'GET_TICKET_COMMENTS', 'GET_TICKET_SUMMARY']);
const Tickets = () => {
  const [t] = useTranslation();
  const { Component } = useComponentsPool();
  const dispatch = useDispatch();
  const { isOnline } = useIsOnline();
  const tickets = useSelector(state => state.tickets.list);
  const notifications = useSelector(state => state.navbar.notificationsList);
  const [width] = useState(`${100 / tickets.length}%`);
  const [ticketMovement, setTicketMovement] = useState({ movement_type: "", destination: "" });
  const notification_sound = useSelector(state => state.workspace.settings.notification_sound);
  const playingSound = useSelector(state => state.users.playingSound);
  const isLoading = useSelector(state => loadingSelector(state.layout.loading));
  const isLoadingOnMobile = useSelector(state => loadingSelectorOnMobile(state.layout.loading));
  const modal = useModal();
  const groupList = useSelector(state => state.groups.list);
  const footbar = useFootBar();
  const workspace = localStorage.getItem('iot_rocket_workspace');
  const ticketsActiveMode = useSelector(state => state.tickets.list.find(category => category?.id === 'ARCHIVED')?.isVisible === false);
  const workspaceSettings = useSelector(state => state.workspace.settings);
  const floorMode = workspaceSettings.mode_floor;
  const positionMode = workspaceSettings.mode_position;
  const [hasLoadedArchivedTicketOnce, setHasLoadedArchivedTicketOnce] = useState(false);
  const currentUser = useSelector(state => state.users.currentUser);
  const kanbanVersionValueForCurrentUser = getKanbanVersionWsValue(workspaceSettings.kanban_version, currentUser);
  const currentUserRole = useSelector(state => state.users.currentUser.workspace_role?.workspace_role_name);
  const location = useLocation();

  const kanbanIframeUrl = useMemo(() => {
    const url = new URL('https://prototypes.merciyanis.dev/kanban');
    url.searchParams.set('token', localStorage.getItem('iot_rocket_access_JWT'));
    url.searchParams.set('deviceId', 'b62318f4d79d0de7c5f36298');
    url.searchParams.set('workspace', window.location.hostname.split('.')[0]);
    if (workspaceSettings.sla_version === 'NEW') {
      url.searchParams.set('sla', 'true');
    }
    const params = new URLSearchParams(location.search);
    const ticketId = params.get('ticketId');
    const source = params.get('source');
    const mode = params.get('mode');
    if (ticketId !== null) {
      url.searchParams.set('ticket', ticketId);
    }
    if (source !== null) {
      url.searchParams.set('source', source);
    }
    if (mode !== null) {
      url.searchParams.set('mode', mode);
    }
    return url.toString();
  }, [workspaceSettings.sla_version, location.search]);

  if (!isMobile) {
    const routes = useSelector(state => state.footbar.routes);
    if (!routes.find(route => route.path === '/tickets' || route.path === '/tickets/:key')) {
      dispatch(addFootbar({ path: '/tickets', component: 'TicketFootbar' }));
      dispatch(addFootbar({ path: '/tickets/:key', component: 'TicketFootbar' }));
    }
  }

  const required_fields = workspaceSettings.required_fields || '';
  const editableFields = useSelector(state => state.tickets.editableFields);

  // Display supervision mode only if isSupervisorMode is True
  // && current user is USER
  // && and he has supervision skills
  const isSupervisorMode = currentUserRole === 'User'
    && workspaceSettings.supervision_skills_to_users
    && currentUser.group_memberships.some(group => group?.group?.custom_field?.type === 'SKILL');
  const [supervisorMode, setSupervisorMode] = useState('my_tickets');

  if (!editableFields.find(editableField => editableField.name === 'ticket_floor')) {
    dispatch(addTicketEditableField({
      name: 'ticket_floor',
      title: 'tickets:floor',
      componentName: 'EditableField',
      renderValue: 'TicketSimpleViewGroup',
      editionComponent: isClosedFloorField(workspaceSettings.name, floorMode) ? 'GroupSelector' : 'TicketInputShowGroupName',
      props: {
        required: required_fields.includes('floor'),
        rules: required_fields.includes('floor') ? ['maxLength-64', 'selectNotEmpty'] : ['maxLength-64'],
        editedValue: isClosedFloorField(workspaceSettings.name, floorMode) ? ['custom_field', 'floor_group_id'] : ['custom_field', 'floor_label'],
        value: isClosedFloorField(workspaceSettings.name, floorMode) ? ['custom_field', 'floor_group_id'] : ['custom_field', 'floor_label'],
        placeholder: '',
        groupType: 'FLOOR',
        modeHierarchy: true,
        parent: ['custom_field', 'batiment_group_id'],
        object: 'ticket',
        multiple: false
      },
      weight: 20
    }));
  }
  if (!editableFields.find(editableField => editableField.name === 'ticket_position')) {
    const rules = required_fields.includes('position') ? ['maxLength-64', 'selectNotEmpty'] : ['maxLength-64'];
    if (!isClosedPositionField(workspaceSettings.name, positionMode)) { rules.push('isAcceptableStringForTicketPosition'); }
    dispatch(addTicketEditableField({
      name: 'ticket_position',
      title: 'tickets:position',
      renderValue: 'TicketSimpleViewGroup',
      editionComponent: isClosedPositionField(workspaceSettings.name, positionMode) ? 'GroupSelector' : 'TicketInputShowGroupName',
      props: {
        required: required_fields.includes('position'),
        rules: rules,
        editedValue: isClosedPositionField(workspaceSettings.name, positionMode) ? ['custom_field', 'position_group_id'] : ['custom_field', 'position_label'],
        value: isClosedPositionField(workspaceSettings.name, positionMode) ? ['custom_field', 'position_group_id'] : ['custom_field', 'position_label'],
        placeholder: '',
        groupType: 'POSITION',
        modeHierarchy: true,
        parent: ['custom_field', 'floor_group_id'],
        object: 'ticket'
      },
      weight: 30
    }));
  }

  useEffect(() => {
    if (!queryString.parse(location.search)?.key) {
      dispatch(setVisibleTickets('DEFAULT')); // by default we display the active ticket's page
    }
  }, []);

  useEffect(() => {
    if (kanbanVersionValueForCurrentUser === 'NEW') return;
    (async () => {
      const filters = queryString.parse(location.search);
      if (filters.key) {
        let ticket;
        await dispatch(getTicketFromKey(filters.key)).then(res => { ticket = res.action.payload.data; });
        if (ticket) {
          segmentTrack(EVENT_OPEN_TICKET, { ticketId: ticket?.id, ticketOpenedByClickingOnIt: false });
          if (!ticket.active) {
            if (!isMobile) { // We can only see archived tickets on PC
              dispatch(setVisibleTickets('ARCHIVED'));
              // We only retrieve the archived tickets once
              if (!hasLoadedArchivedTicketOnce && tickets.find(cat => cat.id === 'ARCHIVED')?.tickets.length === 0) {
                dispatch(getTickets(false, isSupervisorMode, supervisorMode));
                setHasLoadedArchivedTicketOnce(true);
              }
            } else {
              window.history.replaceState({}, '', '/tickets');
              return toast(
                <Toast
                  isConfirm
                  message={t('notifications:not_found_ticket_key')}
                  icon={'frown'}
                  type={'error'}
                />, {
                position: toast.POSITION.BOTTOM_LEFT,
                className: 'normal',
                bodyClassName: 'notification-body grow-font-size',
                progressClassName: 'error-custom-progress-bar'
              }
              );
            }
          }
          const ticketNotifs = notifications.flat().filter(notif => notif.ticketKey == ticket.key);
          // Mark as "read" every notif related to the opened ticket
          if (ticketNotifs && ticket.key) {
            ticketNotifs
              .filter(notif => !notif.seen_at)
              .forEach(notif => dispatch(updateNotification(notif.id, { seen_at: moment().format() })));
          }
          await dispatch(setTicketShownInDetailPopup(ticket, groupList));
          await dispatch(getTicketSummary(ticket?.id, 1));
          await dispatch(getTicketComments(ticket?.id, 1));
          await dispatch(getTicketOperations(ticket?.id, 1));
          modal.update({ name: 'TicketDetailsPopup', item: ticket, withFootBar: true });
        } else {
          toast(
            <Toast
              isConfirm
              message={t('notifications:not_found_ticket_key')}
              icon={'frown'}
              type={'error'}
            />, {
            position: toast.POSITION.BOTTOM_LEFT,
            className: 'normal',
            bodyClassName: 'notification-body grow-font-size',
            progressClassName: 'error-custom-progress-bar'
          }
          );
          window.history.replaceState({}, '', '/tickets');
        }
      }
    })();
  }, [location]);

  const onDragEnd = async result => {
    if (isOnline) {
      const { source, destination, draggableId: draggedTicketId } = result;
      const checkedTicketIDs = footbar.getSelected();
      const checkedTicketObjects = footbar.getList();

      // If the dragged ticket moved to another col or row and has a destination (successful drag case)
      if (!isEqual(source, destination) && !isEmpty(destination)) {
        // dragged ticket exists in the checked tickets (ID) list
        const isGroupDrag = includes(checkedTicketIDs, draggedTicketId);

        if (!isGroupDrag || (isGroupDrag && checkedTicketIDs.length === 1)) { // Single element drag
          await dispatch(moveTicket({ source, destination, draggedTicketId }, tickets));
          if (source.droppableId !== destination.droppableId) {
            dispatch(updateTicket(draggedTicketId, { status: destination.droppableId }));
          }
          // On n'enregistre pas l'ordre des tickets si un ticket est déplacé dans la colonne terminé
          if (destination.droppableId !== 'COMPLETED' || source.droppableId === destination.droppableId) {
            if (source.droppableId === destination.droppableId) {
              posthog.capture('v1_tickets_reordered', {
                dragged_ticket_id: draggedTicketId,
                from_position: source.droppableId,
                to_position: destination.droppableId,
                workspace: workspace,
              })
            }
            setTicketMovement({ movement_type: ticketMovementType.SINGLE_TICKET, destination: '' });
          }
          if (!isEmpty(footbar.getList().filter((ticket) => ticket?.id === draggedTicketId))) {
            const selectedTickets = cloneDeep(footbar.getList());
            selectedTickets?.forEach((ticket) => {
              if (ticket?.id === draggedTicketId) ticket.status = destination.droppableId;
            });
            footbar.update({
              selected: selectedTickets.map((t) => t?.id),
              list: selectedTickets,
              type: 'ticket'
            });
          }
        } else if (isGroupDrag && checkedTicketIDs.length > 1) {
          // Group elements drag
          const isSameColumn = every(checkedTicketObjects, (ticket) => ticket?.status === source.droppableId);
          if (!isSameColumn) {
            dispatch(ticketsMoveDiffColErrorNotif());
            return;
          }
          dispatch(moveTickets({ source, destination, checkedTicketIDs }, tickets));
          const reorderedTickets = footbar.getList().filter(ticket => ticket.status === destination.droppableId);
          if (reorderedTickets.length > 0) {
            posthog.capture('v1_tickets_reordered', {
              dragged_ticket_ids: reorderedTickets.map(ticket => ticket.id),
              from_position: reorderedTickets[0].status,
              to_position: destination.droppableId,
              workspace: workspace,
            })
          }
          // Update the api from useEffect
          setTicketMovement({ movement_type: ticketMovementType.MULTIPLE_TICKETS, destination: destination.droppableId });
        }
      }
    } else {
      dispatch(notification(t('notifications:update_ticket_error'), 'error', 'frown'));
    }
  };


  useEffect(() => {
    if (ticketMovement.movement_type === ticketMovementType.SINGLE_TICKET) {
      dispatch(saveTicketOrder(tickets));
      setTicketMovement({ movement_type: '', destination: '' });
    }
    if (ticketMovement.movement_type === ticketMovementType.MULTIPLE_TICKETS) {
      const checkedTickets = footbar.getList();
      dispatch(updateTickets(checkedTickets, ticketMovement.destination, tickets))
        .then(() => {
          // update selected tickets status in footbar
          const selectedTickets = cloneDeep(checkedTickets);
          selectedTickets?.forEach((ticket) => {
            ticket.status = ticketMovement.destination;
          });
          footbar.update({
            selected: selectedTickets.map((t) => t?.id),
            list: selectedTickets,
            type: 'ticket'
          });
        })
        .catch((error) => {
          if (error.message === 'Network Error') {
            dispatch(networkConnectionErrorNotif());
            // reload page after 3 seconds ( no cache ) => user can see that there is no network connection
            // it will work if state is not persisted
            setTimeout(() => {
              window.location.reload(true);
            }, 3000);
            return;
          }
          dispatch(getTickets()) // rollback to the most updated data from backend and then updating selected tickets
            .then(() => {
              let checkedTickets_copy = cloneDeep(footbar.getList());
              checkedTickets.forEach((checkedTicket, index) => {
                tickets.forEach((column) => {
                  const updatedTicket = column.tickets.find((ticket) => ticket.id === checkedTicket.id)
                  if (updatedTicket) {
                    checkedTickets_copy[index] = { ...checkedTicket, ...updatedTicket }
                  }
                })
              })
              footbar.update({
                selected: checkedTickets_copy.map((t) => t?.id),
                list: checkedTickets_copy,
                type: 'ticket'
              });
            })
        });
      setTicketMovement({ movement_type: '', destination: '' });
    }
  }, [ticketMovement]);

  useEffect(() => {
    if (playingSound && notification_sound) {
      const audio = new Audio(notification_sound);
      audio.play();
      dispatch(setPlayingSoundOff());
    }
  }, [playingSound]);

  // Pool every 30s on /tickets for zeop only because websocket don't seem to work for specific users
  // /!\ This is a temporary workaround, not a fix
  useEffect(() => {
    if (workspace === 'zeop') {
      const intervalId = setInterval(() => {
        dispatch(getTickets(ticketsActiveMode))
      }, 30000)

      return () => clearInterval(intervalId);
    }
  }, [workspace, ticketsActiveMode]);

  // Refresh the old kanban tickets when switching from NEW to OLD on mobile
  const previousKanbanVersion = useRef(kanbanVersionValueForCurrentUser);
  useEffect(() => {
    if (isMobile && previousKanbanVersion.current === 'NEW' && kanbanVersionValueForCurrentUser === 'OLD') {
      dispatch(getTickets(ticketsActiveMode, isSupervisorMode, supervisorMode));
    }
    previousKanbanVersion.current = kanbanVersionValueForCurrentUser;
  }, [kanbanVersionValueForCurrentUser]);

  // Pour mobile
  const selectedStatus = useSelector(state => state.tickets.selectedStatusMobile);
  const devices = useSelector(state => state.devices.list);
  if (isMobile) {
    const category = tickets.find(ticket => ticket.name === selectedStatus);
    const hasBottomNavigation = canSeeMobileBottomNavigation(currentUser, location.pathname, devices, workspaceSettings);
    return (
      <div className="mobile-tickets-page-wrapper">
        {kanbanVersionValueForCurrentUser === 'NEW' ? (
          <iframe
            id="beta-kanban-iframe" // Used to send the fresh access token to the iframe
            className={classnames('beta-kanban-iframe-mobile', { 'with-bottom-navigation': hasBottomNavigation })}
            src={kanbanIframeUrl}
            allow="microphone"
          />
        ) : category && (
          <DragDropContext onDragEnd={onDragEnd}>
            <Component componentName={'LoaderBarTop'} isLoading={isLoadingOnMobile} />
            <DroppableColumn
              width={width}
              key={category?.id}
              category={category}
            />
          </DragDropContext>
        )}
      </div>
    );
  }

  const isBetaKanbanShown = kanbanVersionValueForCurrentUser === 'NEW' && ticketsActiveMode;

  return (
    <div className={classnames('beta-tickets-page-wrapper', { 'beta-tickets-page-wrapper--iframe': isBetaKanbanShown })}>
      <Component componentName={'LoaderBarTop'} isLoading={isLoading} />
      <KanbanVersionToggle
        portalTargetId="navbar-beta-toggle-slot"
        onToggle={version => {
          // Close the V1 Kanban footbar before updating the kanban version
          footbar.close();
          // Refresh tickets when switching from NEW (iframe) back to OLD (kanban)
          if (version === 'NEW') {
            dispatch(getTickets(ticketsActiveMode, isSupervisorMode, supervisorMode));
          }
        }}
      />
      {!isBetaKanbanShown && ( // The beta kanban has its own controls
      <div className={'ticket-links-container'}>
        {isSupervisorMode && (
          <div className={'change-view-mode-buttons'}>
            <Tooltip
              placement={'top'}
              arrow={false}
              title={t('tickets:my_tickets')}
            >
              <IconButton
                onClick={() => {
                  if (supervisorMode !== 'my_tickets') {
                    dispatch(getTickets(ticketsActiveMode, isSupervisorMode, 'my_tickets'));
                    setSupervisorMode('my_tickets');
                  }
                }}
              >
                <Component componentName={'Icon'} type={supervisorMode === 'my_tickets' ? 'userFilled' : 'user'} size="sm" />
              </IconButton>
            </Tooltip>
            <Tooltip
              placement={'top'}
              arrow={false}
              title={t('tickets:their_tickets')}
            >
              <IconButton
                onClick={() => {
                  if (supervisorMode !== 'their_tickets') {
                    dispatch(getTickets(ticketsActiveMode, isSupervisorMode, 'their_tickets'));
                    setSupervisorMode('their_tickets');
                  }
                }}
              >
                <Component componentName={'Icon'} type={supervisorMode === 'their_tickets' ? 'userMultipleFilled' : 'userMultiple'} size="sm" />
              </IconButton>
            </Tooltip>
          </div>
        )}
        <Link
          className="switch-view-page connectors-button"
          underline="hover"
          onClick={() => {
            window.open('https://merciyanis-platform-showcase.lovable.app/', '_blank');
          }}
        >
          {t(`tickets:connectors`)}
        </Link>
        <span className="connectors-separator">|</span>
        <Link
          className="switch-view-page"
          underline="hover"
          onClick={() => {
            if (ticketsActiveMode) {
              dispatch(setVisibleTickets('ARCHIVED'));
              // clean footbar state
              footbar.close()
              // We only retrieve the archived tickets once
              if (!hasLoadedArchivedTicketOnce && tickets.find(cat => cat.id === 'ARCHIVED')?.tickets.length === 0) {
                dispatch(getTickets(false, isSupervisorMode, supervisorMode));
                setHasLoadedArchivedTicketOnce(true);
              } else if (isSupervisorMode) {
                dispatch(getTickets(false, isSupervisorMode, supervisorMode));
              }
            } else {
              dispatch(setVisibleTickets('DEFAULT'));
              if (isSupervisorMode) {
                dispatch(getTickets(true, isSupervisorMode, supervisorMode));
              }
            }
          }}
        >
          {t(`tickets:see_tickets_${ticketsActiveMode ? 'archived' : 'active'}`)}
        </Link>
      </div >
      )}

      {
        ticketsActiveMode
          ? ( // KANBAN-V2
            kanbanVersionValueForCurrentUser === 'NEW' ? (
              <iframe
                id="beta-kanban-iframe" // Used to send the fresh access token to the iframe
                className="beta-kanban-iframe"
                src={kanbanIframeUrl}
                allow="microphone"
              />
            ) : (
            <div className={classnames('tickets-container tickets-container-with-top-link')} >
              <DragDropContext onDragEnd={onDragEnd}>
                {tickets.filter(cat => cat.id !== 'ARCHIVED').map(category => (
                  <DroppableColumn
                    width={width}
                    key={category?.id}
                    category={category}
                  />
                ))}
              </DragDropContext>
            </div >
            )
          ) : <Component componentName={'ArchivedTicketsTable'} />
      }
    </div>
  );
};

export default React.memo(Tickets);
